import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Separator,
    StatTile,
    Timeline,
    Button,
    Alert,
    Badge,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    Check,
    Bell,
    Code,
    Mail,
    Rss
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const BellIcon = createKitIcon(Bell);

const CheckIcon = createKitIcon(Check);

const MailIcon = createKitIcon(Mail);

const RssIcon = createKitIcon(Rss);

const CodeIcon = createKitIcon(Code);

const DAYS = 90;

const TODAY = new Date(2026, 10, 1);

const MS_PER_DAY = 86400000;

const SERVICES = [
    {
        host: "api.nibgat.com",
        incidents: {
            58: "partial"
        },
        uptime: 99.98,
        key: "api"
    },
    {
        host: "app.nibgat.com",
        incidents: {},
        uptime: 100,
        key: "web"
    },
    {
        host: "push.nibgat.com",
        key: "notifications",
        incidents: {
            71: "partial"
        },
        uptime: 99.96
    },
    {
        host: "files.nibgat.com",
        incidents: {
            52: "outage"
        },
        uptime: 99.9,
        key: "files"
    },
    {
        host: "pay.nibgat.com",
        incidents: {
            81: "partial"
        },
        key: "payments",
        uptime: 99.99
    }
] as Array<{
    incidents: Record<number, string>;
    uptime: number;
    host: string;
    key: string;
}>;

const INCIDENTS = [
    {
        date: new Date(2026, 9, 21),
        service: "payments",
        key: "payments",
        badges: [
            "partial"
        ],
        minutes: 14
    },
    {
        date: new Date(2026, 9, 3),
        service: "notifications",
        key: "notifications",
        badges: [
            "partial"
        ],
        minutes: 47
    },
    {
        date: new Date(2026, 8, 9),
        service: "filesApi",
        key: "files",
        badges: [
            "outage"
        ],
        minutes: 130
    }
];

const STEPS = [
    "resolved",
    "identified",
    "investigating"
];

const LEGEND = [
    "up",
    "partial",
    "outage"
];

const BAR_TYPES = {
    partial: "warning",
    outage: "danger",
    up: "success"
} as const;

const stylesheet = StyleSheet.create({
    bar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    between: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    serviceHead: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    update: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    strip: {
        alignItems: "stretch",
        flexDirection: "row"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrap: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        alignItems: "center",
        flexGrow: 1
    },
    headRight: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    tiles: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    tile: {
        flexBasis: 200,
        flexShrink: 1,
        minWidth: 0,
        flexGrow: 1
    },
    stripBar: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    grow: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    section: {
        width: "100%"
    },
    legendDot: {
        flexShrink: 0
    }
});

const FOOTER_LINKS = [
    "privacy",
    "terms",
    "site"
];

const handlePress = () => undefined;

const Status01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        isSubscribed,
        setIsSubscribed
    ] = useState(false);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const dayFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short",
        day: "numeric"
    });

    const fullDayFormat = new Intl.DateTimeFormat(activeLocale, {
        year: "numeric",
        month: "short",
        day: "numeric"
    });

    const percentFormat = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 2,
        style: "percent"
    });

    const handleSubscribe = () => setIsSubscribed((previous) => !previous);

    const getFill = (state: string) => {
        if(state === "outage") {
            return colors.content.container.danger;
        }

        if(state === "partial") {
            return colors.content.container.warning;
        }

        return colors.content.container.success;
    };

    const renderBar = (service: typeof SERVICES[number], index: number) => {
        const state = service.incidents[index] ?? "up";

        const date = new Date(TODAY.getTime() - (DAYS - 1 - index) * MS_PER_DAY);

        return <View
            accessibilityLabel={state === "up" ? undefined : `${dayFormat.format(date)} · ${t(`blocks.status01.state.${state}`)}`}
            style={[
                stylesheet.stripBar,
                {
                    backgroundColor: getFill(state),
                    height: controlHeights.large,
                    borderRadius: radiuses.xs
                }
            ]}
            accessible={state !== "up"}
            key={index}
        />;
    };

    const renderService = (service: typeof SERVICES[number]) => <View
        style={{
            gap: spaces.spacingSm
        }}
    >
        <View
            style={[
                stylesheet.serviceHead,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <View
                style={stylesheet.grow}
            >
                <Text
                    variant="titleSmallSize"
                    numberOfLines={1}
                    color="high"
                >
                    {t(`blocks.status01.service.${service.key}`)}
                </Text>
                <Text
                    variant="monoMediumSize"
                    numberOfLines={1}
                    color="mid"
                >
                    {service.host}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.headRight,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    !isCompact && <Badge
                        title={t("blocks.status01.operational")}
                        icon={CheckIcon}
                        variant="tonal"
                        type="success"
                        size="small"
                    />
                }
                <Text
                    variant="labelLargeSize"
                    color="high"
                >
                    {percentFormat.format(service.uptime / 100)}
                </Text>
            </View>
        </View>
        <View
            style={[
                stylesheet.strip,
                {
                    gap: spaces.spacingXxs
                }
            ]}
        >
            {Array.from({
                length: DAYS
            }, (_, index) => renderBar(service, index))}
        </View>
        <View
            style={stylesheet.between}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.status01.daysAgo")}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.status01.today")}
            </Text>
        </View>
    </View>;

    const renderLegend = (state: string) => <View
        style={[
            stylesheet.row,
            {
                gap: spaces.spacingXs
            }
        ]}
        key={state}
    >
        <View
            style={[
                stylesheet.legendDot,
                {
                    backgroundColor: getFill(state),
                    borderRadius: radiuses.xs,
                    height: spaces.spacingSm,
                    width: spaces.spacingSm
                }
            ]}
        />
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.status01.state.${state}`)}
        </Text>
    </View>;

    const renderFooterLink = (key: string) => <Button
        title={t(`blocks.status01.${key}`)}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
        key={key}
    />;

    const renderUpdate = (incident: typeof INCIDENTS[number], step: string) => <View
        style={[
            stylesheet.update,
            {
                gap: spaces.spacingMd
            }
        ]}
        key={step}
    >
        <Text
            variant="monoMediumSize"
            color="mid"
        >
            {t(`blocks.status01.incident.${incident.key}.${step}.time`)}
        </Text>
        <Text
            variant="bodyMediumSize"
            style={stylesheet.grow}
            color="high"
        >
            <Text
                variant="labelLargeSize"
                color="high"
            >
                {`${t(`blocks.status01.step.${step}`)} `}
            </Text>
            {t(`blocks.status01.incident.${incident.key}.${step}.text`)}
        </Text>
    </View>;

    const renderIncident = (incident: typeof INCIDENTS[number]) => <Timeline.Item
        title={<View
            style={{
                gap: spaces.spacingXs
            }}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {fullDayFormat.format(incident.date)}
            </Text>
            <View
                style={[
                    stylesheet.wrap,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {t(`blocks.status01.incident.${incident.key}.title`)}
                </Text>
                <Badge
                    title={t("blocks.status01.resolved")}
                    icon={CheckIcon}
                    variant="tonal"
                    type="success"
                    size="small"
                />
                {
                    incident.badges.map((badge) => <Badge
                        type={BAR_TYPES[badge as keyof typeof BAR_TYPES]}
                        title={t(`blocks.status01.state.${badge}`)}
                        variant="tonal"
                        size="small"
                        key={badge}
                    />)
                }
                <Badge
                    title={t(`blocks.status01.service.${incident.service}`)}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.status01.minutes", [
                        incident.minutes
                    ])}
                </Text>
            </View>
        </View>}
        key={incident.key}
        icon={CheckIcon}
        type="success"
    >
        <View
            style={{
                paddingBottom: spaces.spacingMd,
                gap: spaces.spacingSm
            }}
        >
            {STEPS.map((step) => renderUpdate(incident, step))}
        </View>
    </Timeline.Item>;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
    >
        <View
            style={[
                stylesheet.bar,
                stylesheet.section,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <BrandMark />
                {
                    !isCompact && <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.status01.title")}
                    </Text>
                }
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    !isCompact && <Button
                        title={t("blocks.status01.history")}
                        onPress={handlePress}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                }
                <Button
                    title={t(isSubscribed ? "blocks.status01.subscribed" : "blocks.status01.subscribe")}
                    onPress={handleSubscribe}
                    icon={BellIcon}
                    size="small"
                />
            </View>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.section,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <Alert
                title={t("blocks.status01.allOperational")}
                description={t("blocks.status01.updated")}
                icon={CheckIcon}
                type="success"
            />
            <View
                style={[
                    stylesheet.tiles,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <StatTile
                    style={stylesheet.tile}
                    variant="outline"
                >
                    <StatTile.Title>
                        {t("blocks.status01.tileUptime")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {percentFormat.format(0.9997)}
                    </StatTile.Value>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.status01.tileUptimeNote")}
                    </Text>
                </StatTile>
                <StatTile
                    style={stylesheet.tile}
                    variant="outline"
                >
                    <StatTile.Title>
                        {t("blocks.status01.tileLatency")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {t("blocks.status01.tileLatencyValue")}
                    </StatTile.Value>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.status01.tileLatencyNote")}
                    </Text>
                </StatTile>
                <StatTile
                    style={stylesheet.tile}
                    variant="outline"
                >
                    <StatTile.Title>
                        {t("blocks.status01.tileIncident")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {t("blocks.status01.tileIncidentValue")}
                    </StatTile.Value>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.status01.tileIncidentNote")}
                    </Text>
                </StatTile>
            </View>
            <Card
                variant="outline"
            >
                <Card.Header>
                    <Card.Title>
                        {t("blocks.status01.components")}
                    </Card.Title>
                    <Card.Action>
                        <View
                            style={[
                                stylesheet.wrap,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {LEGEND.map(renderLegend)}
                            <Badge
                                title={t("blocks.status01.range")}
                                variant="outline"
                                type="neutral"
                                size="small"
                            />
                        </View>
                    </Card.Action>
                </Card.Header>
                {
                    SERVICES.map((service) => <View
                        key={service.key}
                    >
                        <Separator />
                        <Card.Content>
                            {renderService(service)}
                        </Card.Content>
                    </View>)
                }
                <Separator />
                <Card.Footer
                    align="between"
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.status01.timezone")}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.status01.method")}
                    </Text>
                </Card.Footer>
            </Card>
            <View
                style={[
                    stylesheet.bar,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View>
                    <Text
                        variant="headlineSmallSize"
                        role="heading"
                        color="high"
                    >
                        {t("blocks.status01.pastIncidents")}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.status01.pastLead")}
                    </Text>
                </View>
                <Button
                    title={t("blocks.status01.allHistory")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                />
            </View>
            <Card
                variant="outline"
            >
                <Card.Content>
                    <Timeline
                        accessibilityLabel={t("blocks.status01.pastIncidents")}
                    >
                        {INCIDENTS.map(renderIncident)}
                    </Timeline>
                </Card.Content>
            </Card>
            <Card
                variant="filled"
            >
                <Card.Content>
                    <View
                        style={[
                            stylesheet.bar,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={stylesheet.grow}
                        >
                            <Text
                                variant="titleMediumSize"
                                color="high"
                            >
                                {t("blocks.status01.notifyTitle")}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.status01.notifyText")}
                            </Text>
                        </View>
                        <View
                            style={[
                                stylesheet.wrap,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Button
                                title={t("blocks.status01.email")}
                                onPress={handlePress}
                                variant="outline"
                                icon={MailIcon}
                                type="neutral"
                            />
                            <Button
                                title={t("blocks.status01.rss")}
                                onPress={handlePress}
                                variant="outline"
                                type="neutral"
                                icon={RssIcon}
                            />
                            <Button
                                title={t("blocks.status01.webhook")}
                                onPress={handlePress}
                                variant="outline"
                                icon={CodeIcon}
                                type="neutral"
                            />
                        </View>
                    </View>
                </Card.Content>
            </Card>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.bar,
                stylesheet.section,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.status01.footer")}
            </Text>
            <View
                style={[
                    stylesheet.wrap,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                {FOOTER_LINKS.map(renderFooterLink)}
            </View>
        </View>
    </ScrollView>;
};

export default Status01;
